import { useState, useEffect } from 'react';
import '../App.css';

function AdminRecovery() {
    const [failedTransactions, setFailedTransactions] = useState([]);
    const [processing, setProcessing] = useState(null);
    const [results, setResults] = useState({});
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [authError, setAuthError] = useState('');

    useEffect(() => {
        if (isAuthenticated) {
            loadFailedTransactions();
        }
    }, [isAuthenticated]);

    const loadFailedTransactions = () => {
        const failures = JSON.parse(localStorage.getItem('failed_transactions') || '[]');
        setFailedTransactions(failures);
    };

    const retryTransaction = async (transaction, index) => {
        setProcessing(index);

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                custid: transaction.custid,
                transaction_id: transaction.transaction_id,
                intent: transaction.intent,
                method: transaction.method,
                value: transaction.value,
                email: transaction.email,
                amount: transaction.amount,
                retry_attempt: true
            })
        };

        try {
            const response = await fetch(process.env.REACT_APP_SERVER + `/sms/transaction.php`, requestOptions);
            
            if (response.ok) {
                const result = await response.json();
                if (!result.error && result.status !== 'error') {
                    setResults({...results, [index]: 'success'});
                    removeTransaction(index);
                } else {
                    setResults({...results, [index]: `failed: ${result.message || 'Unknown error'}`});
                }
            } else {
                setResults({...results, [index]: `failed: HTTP ${response.status}`});
            }
        } catch (error) {
            setResults({...results, [index]: `failed: ${error.message}`});
        }

        setProcessing(null);
    };

    const removeTransaction = (index) => {
        const updated = failedTransactions.filter((_, i) => i !== index);
        setFailedTransactions(updated);
        localStorage.setItem('failed_transactions', JSON.stringify(updated));
    };

    const clearAll = () => {
        if (window.confirm('Are you sure you want to clear all failed transactions?')) {
            localStorage.removeItem('failed_transactions');
            setFailedTransactions([]);
            setResults({});
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        setAuthError('');
        
        const adminUsername = process.env.REACT_APP_ADMIN_USERNAME;
        const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
        
        if (!adminUsername || !adminPassword) {
            setAuthError('Admin credentials not configured. Please set REACT_APP_ADMIN_USERNAME and REACT_APP_ADMIN_PASSWORD environment variables.');
            return;
        }
        
        if (username === adminUsername && password === adminPassword) {
            setIsAuthenticated(true);
            sessionStorage.setItem('adminAuthenticated', 'true');
        } else {
            setAuthError('Invalid username or password');
        }
    };

    // Check for existing session
    useEffect(() => {
        const existingAuth = sessionStorage.getItem('adminAuthenticated');
        if (existingAuth === 'true') {
            setIsAuthenticated(true);
        }
    }, []);

    const handleLogout = () => {
        setIsAuthenticated(false);
        sessionStorage.removeItem('adminAuthenticated');
        setUsername('');
        setPassword('');
    };

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <form onSubmit={handleLogin} style={{ padding: '40px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9', width: '300px' }}>
                    <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Admin Login</h2>
                    
                    {authError && (
                        <div style={{ color: 'red', marginBottom: '15px', textAlign: 'center', fontSize: '14px' }}>
                            {authError}
                        </div>
                    )}
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Username:</label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            required
                        />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>Password:</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                            required
                        />
                    </div>
                    
                    <button
                        type="submit"
                        style={{ width: '100%', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Login
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1>Failed Transaction Recovery</h1>
                <button onClick={handleLogout} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Logout
                </button>
            </div>
            <p>Total failed transactions: {failedTransactions.length}</p>
            
            {failedTransactions.length > 0 && (
                <button onClick={clearAll} style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                    Clear All
                </button>
            )}

            {failedTransactions.length === 0 ? (
                <p>No failed transactions found.</p>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Timestamp</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Customer ID</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Amount (Credits)</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Value (£)</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Stripe Intent</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Error</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '12px', textAlign: 'left' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {failedTransactions.map((transaction, index) => (
                            <tr key={index}>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>
                                    {new Date(transaction.timestamp).toLocaleString()}
                                </td>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>{transaction.custid}</td>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>{transaction.amount}</td>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>£{transaction.value}</td>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px', fontSize: '12px', fontFamily: 'monospace' }}>
                                    {transaction.intent}
                                </td>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>{transaction.error}</td>
                                <td style={{ border: '1px solid #dee2e6', padding: '12px' }}>
                                    {processing === index ? (
                                        <span>Processing...</span>
                                    ) : results[index] ? (
                                        <span style={{ color: results[index] === 'success' ? 'green' : 'red' }}>
                                            {results[index]}
                                        </span>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => retryTransaction(transaction, index)}
                                                style={{ marginRight: '5px', padding: '5px 10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                            >
                                                Retry
                                            </button>
                                            <button
                                                onClick={() => removeTransaction(index)}
                                                style={{ padding: '5px 10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                            >
                                                Remove
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                <h3>Instructions for Support Team:</h3>
                <ol>
                    <li>Each failed transaction represents a successful Stripe payment where credits were not added</li>
                    <li>Click "Retry" to attempt to add the credits again</li>
                    <li>The Stripe Intent ID can be verified in the Stripe Dashboard</li>
                    <li>Only remove transactions after verifying credits were manually added</li>
                    <li>Access this page at: /admin-recovery (requires authentication in production)</li>
                </ol>
            </div>
        </div>
    );
}

export default AdminRecovery;